import { GroupSecurity } from '@quromedical/fhir-common';
import { AdmissionGetResponse } from '@quromedical/models';
import { first } from '@quromedical/utils';
import { PatientHeaderSection } from 'core/patient';
import { Alert, SkeletonProvider, Snackbar, Tabs } from 'design-system';
import { useUserSession } from 'hooks/useUserSession';
import { AdmissionApi, admissionSWRKey } from 'integration/aggregate';
import { PatientDrawerScreenProps } from 'navigation';
import { usePatientId } from 'providers/PatientIdContext';
import React, { useCallback } from 'react';
import { ScrollView } from 'react-native';
import { strings } from 'strings';
import useSWR from 'swr';

import { PatientMedicationsTab } from './tabs';

const admissionApi = new AdmissionApi();
export const PatientMedicationScreen: React.FC<PatientDrawerScreenProps<'Medication'>> = () => {
  const id = usePatientId();
  const session = useUserSession();

  const { data, isValidating, error, mutate } = useSWR(
    admissionSWRKey,
    async (): Promise<AdmissionGetResponse | undefined> => {
      const patientAdmissions = await admissionApi.getAdmissions({
        patient: id,
        status: 'in-progress',
      });
      const admission = first(patientAdmissions.admissions);

      return admission;
    }
  );

  const revalidate = useCallback(() => mutate, [mutate]);

  const admissionId = data?.id;

  const hasMedicationPermissions = session.hasAny([GroupSecurity.PatientRead]);

  if (error) {
    return (
      <Snackbar onClose={revalidate} isOpen={!!error}>
        <Alert
          backgroundColor="status-critical"
          textColor="white"
          onAction={revalidate}
          actionIcon="close"
          title={strings.ErrorCardTitle}
          body={strings.GenericErrorMessage}
        />
      </Snackbar>
    );
  }

  return (
    <ScrollView>
      <PatientHeaderSection id={id} />

      <SkeletonProvider loading={isValidating}>
        <Tabs.Group>
          <Tabs.Screen isHidden={!hasMedicationPermissions} title={strings.MedicationsCardTitle}>
            <PatientMedicationsTab patientId={id} admissionId={admissionId} />
          </Tabs.Screen>
        </Tabs.Group>
      </SkeletonProvider>
    </ScrollView>
  );
};
